import { createFileRoute } from "@tanstack/react-router"
import {
  getPagedWorkingTimeBookings,
  WorkingTimeBookingDto,
  WorkingTimeImportStatus,
} from "@/orval/gen"
import { PersonName } from "@components/PersonName"
import { Page } from "@components/Page"
import { getFormattedDate } from "@/utils/get-formatted-date"
import {
  InfiniteScrollingTable,
  RowProps,
} from "@/routes/-components/InfiniteScrollingTable"
import { HideableFilterGroup } from "../../-components/hideable-filter-group"
import { DatePickerInput, DatesRangeValue } from "@mantine/dates"
import { MultiSelect, Stack, Text } from "@mantine/core"
import { InfinitePersonSingleSelect } from "@components/SelectFromQuery/InfinitePersonSingleSelect"
import { ColumnDef, Row, SortingState } from "@tanstack/react-table"
import { match } from "ts-pattern"
import dayjs from "dayjs"
import { WorkingTimeImportStatusBadge } from "../../-components/working-time-bookings/WorkingTimeImportStatusBadge"
import { WorkingTimeBookingRequestDetailsModal } from "./-components/WorkingTImeBookingRequestDetailsModal"
import { DepartmentWorkAreaTree } from "./-components/DepartmentWorkAreaTree"
import { useBreakpoints } from "@/utils/use-breakpoints"
import { WorkAreaName } from "@components/WorkAreaName"
import { useTranslation } from "react-i18next"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"

interface SearchParams {
  dateRange?: DatesRangeValue<string>
  personId?: number
  status?: WorkingTimeImportStatus
  workAreaIds?: string[]
  statuses?: WorkingTimeImportStatus[]
  sorting?: SortingState
}

export const Route = createFileRoute("/app/duties/working-time-booking/")({
  component: RouteComponent,
  validateSearch: (search: SearchParams) => ({
    ...search,
    sorting: search.sorting || [{ id: "timeStamp", desc: true }],
  }),
})

function RouteComponent() {
  const { t } = useTranslation("app_server")
  const today = dayjs()

  const [filterVisible, setFilterVisible] = useUserLocalStorage({
    key: "working-time-booking_filter-visible",
    defaultValue: false,
  })
  const search = Route.useSearch()
  const [dateRange, setDateRange] = React.useState<DatesRangeValue<string>>(
    search.dateRange ?? [null, null]
  )
  const activeFilters = Object.entries(search).filter(
    ([name, value]) => name !== "sorting" && !!value
  ).length
  const navigate = Route.useNavigate()
  const [selectedRequest, setSelectedRequest] = React.useState<
    WorkingTimeBookingDto | undefined
  >()

  React.useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      navigate({
        search: {
          ...search,
          dateRange: dateRange,
        },
        replace: true,
      })
    }
    if (!dateRange[0] && !dateRange[1] && search.dateRange) {
      navigate({
        search: {
          ...search,
          dateRange: undefined,
        },
        replace: true,
      })
    }
  }, [dateRange])

  const getRowProps = React.useCallback(function (
    row: Row<WorkingTimeBookingDto>
  ): RowProps | undefined {
    if (!row.original.canApprove) return undefined

    const status = row.original.status

    return match<WorkingTimeImportStatus, RowProps | undefined>(status)
      .with("Requested", "Approved", "Rejected", () => {
        return {
          onClick: () => setSelectedRequest(row.original),
          bd: "solid 1px",
        }
      })
      .otherwise(() => undefined)
  }, [])

  const columnDefinitions = React.useMemo<ColumnDef<WorkingTimeBookingDto>[]>(
    () => [
      ...(search.personId
        ? []
        : [
            {
              accessorKey: "personId",
              cell: ({ row }) => (
                <PersonName personId={row.original.personId} />
              ),
              header: t("Person"),
            } as ColumnDef<WorkingTimeBookingDto>,
          ]),
      {
        accessorKey: "workAreaId",
        header: t("Department"),
        cell: ({ row }) =>
          !!row.original.workAreaId && (
            <WorkAreaName workAreaId={row.original.workAreaId} />
          ),
      },
      {
        accessorKey: "timeStamp",
        header: t("Time"),
        cell: ({ row }) =>
          getFormattedTimestamp(row.original.timeStamp, search.dateRange),
      },
      { accessorKey: "type.name", header: t("Type") },
      { accessorKey: "source", header: t("Source") },
      { accessorKey: "note", header: t("Note") },
      {
        accessorKey: "status",
        header: t("Status"),
        cell: ({ row }) => (
          <WorkingTimeImportStatusBadge status={row.original.status} />
        ),
      },
    ],
    [search.personId, search.dateRange]
  )
  const { xs } = useBreakpoints()
  const filterWidth = xs ? "100%" : 250
  return (
    <Page
      buttonGroupProps={{
        children: (
          <HideableFilterGroup.Button
            activeFilters={activeFilters}
            visible={filterVisible}
            onChange={() => setFilterVisible(!filterVisible)}
          />
        ),
      }}
    >
      <Stack h={"100%"} gap={"xs"}>
        <HideableFilterGroup w={"100%"} visible={filterVisible}>
          <InfinitePersonSingleSelect
            value={search.personId?.toString()}
            onSelectionChange={(v) =>
              navigate({
                search: {
                  ...search,
                  personId: v === null ? undefined : parseInt(v),
                },
                replace: true,
              })
            }
            inputProps={{ w: filterWidth }}
          />
          <DatePickerInput
            label={t("TimePeriod")}
            type="range"
            value={dateRange}
            valueFormat="L"
            allowSingleDateInRange
            clearable
            onChange={setDateRange}
            w={filterWidth}
            presets={[
              {
                value: [today.format("YYYY-MM-DD"), today.format("YYYY-MM-DD")],
                label: t("Today"),
              },
              {
                value: [
                  today.subtract(1, "day").format("YYYY-MM-DD"),
                  today.subtract(1, "day").format("YYYY-MM-DD"),
                ],
                label: t("Yesterday"),
              },
              {
                value: [
                  today.subtract(2, "day").format("YYYY-MM-DD"),
                  today.format("YYYY-MM-DD"),
                ],
                label: t("LastTwoDays"),
              },
              {
                value: [
                  today.subtract(7, "day").format("YYYY-MM-DD"),
                  today.format("YYYY-MM-DD"),
                ],
                label: t("Last7Days"),
              },
            ]}
          />
          <DepartmentWorkAreaTree
            maxPills={1}
            initialCheckedState={search.workAreaIds}
            onCheckedStateChange={(v) =>
              navigate({
                search: {
                  ...search,
                  workAreaIds: v && v.length > 0 ? v : undefined,
                },
                replace: true,
              })
            }
            w={filterWidth}
          />
          <MultiSelect
            clearable
            label={t("Status")}
            w={filterWidth}
            data={Object.values(WorkingTimeImportStatus).map((status) => ({
              value: status,
              label: t(`WorkingTimeImportStatus_${status}`),
            }))}
            value={search.statuses ? search.statuses : []}
            onChange={(v) =>
              navigate({
                search: {
                  ...search,
                  statuses:
                    v.length > 0 ? (v as WorkingTimeImportStatus[]) : undefined,
                },
                replace: true,
              })
            }
          />
        </HideableFilterGroup>
        <InfiniteScrollingTable
          infiniteQueryResult={useDefaultInfiniteQuery(
            getPagedWorkingTimeBookings,
            (pageRequest) => ({
              ...search,
              from: search.dateRange?.[0] ?? undefined,
              to: search.dateRange?.[1] ?? undefined,
              ...pageRequest,
            }),
            {
              queryKey: ["pagedWorkingTimeBookings", search],
            }
          )}
          columnDefinitions={columnDefinitions}
          getRowProps={getRowProps}
          sorting={search.sorting}
          onSortingChange={(sorting) =>
            navigate({ search: { ...search, sorting }, replace: true })
          }
        />
      </Stack>
      <WorkingTimeBookingRequestDetailsModal
        request={selectedRequest!}
        opened={!!selectedRequest}
        onClose={() => setSelectedRequest(undefined)}
      />
    </Page>
  )
}

function getFormattedTimestamp(
  timestamp: string,
  dateRange?: DatesRangeValue<string>
) {
  if (dateRange && dateRange[0] && dateRange[1]) {
    const start = dayjs(dateRange[0])
    const end = dayjs(dateRange[1])
    if (start.isSame(end, "date")) {
      if (dayjs(timestamp).isSame(start, "date")) {
        return dayjs(timestamp).format("LT")
      } else {
        return dayjs(timestamp).format("LT +1")
      }
    }
  }
  return getFormattedDate(timestamp, "L LT")
}
